import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  ColorSeccion,
  EntradaGuardarPregunta,
  Escala,
  Pregunta,
  TipoPregunta,
  Version,
} from '@copathy/shared';
import {
  COLORES_SECCION,
  esSeccion,
  FAMILIAS_PREGUNTA,
  TIPO_PREGUNTA,
  TIPOS_PREGUNTA,
  usaEscala,
  usaItems,
  usaRango,
} from '@copathy/shared';
import { Boton } from '../componentes/Boton';
import { BotonVolver } from '../componentes/BotonVolver';
import { Campo } from '../componentes/Campo';
import { Combo } from '../componentes/Combo';
import { Contenedor } from '../componentes/Contenedor';
import { VistaFormulario } from '../componentes/VistaFormulario';
import { ACENTOS } from '../componentes/PreguntaRespondible';
import { Equis, Flecha } from '../componentes/iconos';
import { useMigas } from '../lib/migas';
import { usePuede } from '../lib/auth';
import { ErrorApi } from '../lib/api';
import { useCatalogo, useGuardarOpcion } from '../lib/configuracion';
import {
  useBorrador,
  useCuestionario,
  useEscalas,
  useVersion,
  useVersiones,
} from '../lib/cuestionarios';

/**
 * M5 — el constructor: herramienta a la izquierda, **un teléfono** a la derecha.
 *
 * ── La idea que gobierna la pantalla ──
 * **Lo publicado se mira; lo que se edita es siempre un borrador.** Si una
 * versión respondida se pudiera editar, el «antes vs. después» compararía cosas
 * distintas sin que nada falle. Lo impide el motor (D-049); acá se hace visible.
 *
 * ── Por qué el teléfono ──
 * El trabajador responde desde la app móvil (D-011). La previa dibuja el
 * formulario DENTRO de un teléfono, paginado y navegable, porque probar el
 * recorrido es probar el instrumento — y porque es el adelanto de la app móvil
 * que aún no existe: cuando exista, esto es lo que la valida.
 *
 * ── El selector de versiones es una frase, no chips ──
 * Los chips «v3 · borrador / v2 · publicada / v1 · retirada» parecían filtros y
 * nadie sabía para qué eran (lo dijo Jonathan). Ahora hay UNA insignia de estado
 * y, si hay más versiones, un desplegable que dice qué es cada una en palabras.
 */

export function CuestionarioArmar() {
  const { id: idTexto } = useParams();
  const id = Number(idTexto);

  const cuestionario = useCuestionario(id);
  const versiones = useVersiones(id);
  const acciones = useBorrador(id);
  const escalas = useEscalas();
  const puedeArmar = usePuede('cuestionarios.armar');

  const [error, setError] = useState<string | null>(null);
  const [elegida, setElegida] = useState<number | null>(null);
  const [edicion, setEdicion] = useState<{ preguntaId: number | null; f: Borrador } | null>(null);

  /*
    Qué versión se mira. Por omisión el borrador si hay uno y quien mira puede
    editar. `elegida` gana solo si apunta a una versión que todavía existe —
    descartar un borrador deja el id colgando de una fila borrada.
  */
  const lista = versiones.data ?? [];
  const porOmision =
    (puedeArmar ? lista.find((v) => v.estado === 'borrador') : undefined) ??
    lista.find((v) => v.estado === 'publicada') ??
    lista[0];
  const versionId =
    (elegida !== null && lista.some((v) => v.id === elegida) ? elegida : null) ??
    porOmision?.id ??
    null;

  const version = useVersion(id, versionId);

  useMigas([
    { texto: 'Cuestionarios', a: '/cuestionarios' },
    { texto: cuestionario.data?.nombre ?? 'Cuestionario' },
  ]);

  // Cambiar de versión con el editor abierto dejaría un formulario apuntando a
  // una pregunta de otra versión.
  useEffect(() => setEdicion(null), [versionId]);

  /*
    Lo que ve el teléfono: las preguntas guardadas, con la que se edita
    sustituida por su versión en vivo.

    ⚠️ Este hook va ANTES de los `return` de carga: un hook tras un `return`
    condicional tumba la pantalla con «Rendered more hooks...», y ni TypeScript
    ni el build lo ven — se descubrió abriendo la página.
  */
  const guardadas = version.data?.preguntas;
  const preguntasEnPantalla: Pregunta[] = useMemo(() => {
    const base = guardadas ?? [];
    if (!edicion) return base;
    const enVivo = aPreguntaPrevia(edicion.f, edicion.preguntaId ?? -1, 0);
    if (edicion.preguntaId === null) {
      return [...base, { ...enVivo, orden: base.length + 1 }];
    }
    return base.map((p) => (p.id === edicion.preguntaId ? { ...enVivo, orden: p.orden } : p));
  }, [guardadas, edicion]);

  const idResaltado = edicion ? (edicion.preguntaId ?? -1) : null;

  if (cuestionario.isPending || versiones.isPending || version.isPending) {
    return (
      <Contenedor ancho="ancho">
        <p className="py-16 text-center text-marino-400">Cargando…</p>
      </Contenedor>
    );
  }

  if (cuestionario.isError || !cuestionario.data || version.isError || !version.data) {
    return (
      <Contenedor ancho="ancho">
        <BotonVolver a="/cuestionarios">Cuestionarios</BotonVolver>
        <p className="py-16 text-center text-red-600">No encontramos este cuestionario.</p>
      </Contenedor>
    );
  }

  const c = cuestionario.data;
  const v: Version = version.data;
  const esBorrador = v.estado === 'borrador';

  async function ejecutar(fn: () => Promise<unknown>, generico: string) {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : generico);
    }
  }

  async function ejecutarYMirar(fn: () => Promise<Version>, generico: string) {
    setError(null);
    try {
      setElegida((await fn()).id);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : generico);
    }
  }

  return (
    <Contenedor ancho="completo" className="px-1">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <BotonVolver a="/cuestionarios">Cuestionarios</BotonVolver>

        {puedeArmar && (
          <div className="flex flex-wrap gap-2">
            {esBorrador ? (
              <>
                {c.versionPublicada !== null && (
                  <Boton
                    variante="secundario"
                    className="px-4 py-2.5 text-sm"
                    cargando={acciones.descartar.isPending}
                    onClick={() => {
                      if (!confirm('Se pierde todo lo que lleva este borrador. ¿Descartarlo?')) {
                        return;
                      }
                      setElegida(null);
                      void ejecutar(
                        () => acciones.descartar.mutateAsync(),
                        'No pudimos descartar el borrador.',
                      );
                    }}
                  >
                    Descartar borrador
                  </Boton>
                )}
                <Boton
                  className="px-4 py-2.5 text-sm"
                  cargando={acciones.publicar.isPending}
                  onClick={() =>
                    void ejecutarYMirar(
                      () => acciones.publicar.mutateAsync(),
                      'No pudimos publicar el cuestionario.',
                    )
                  }
                >
                  Publicar
                </Boton>
              </>
            ) : (
              <Boton
                className="px-4 py-2.5 text-sm"
                cargando={acciones.abrir.isPending}
                onClick={() =>
                  void ejecutarYMirar(
                    () => acciones.abrir.mutateAsync(),
                    'No pudimos abrir el borrador.',
                  )
                }
              >
                {c.tieneBorrador
                  ? 'Seguir editando el borrador'
                  : `Editar — crea la versión ${Math.max(...lista.map((x) => x.version)) + 1}`}
              </Boton>
            )}
          </div>
        )}
      </div>

      <header className="mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-marino-800">{c.nombre}</h1>
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              v.estado === 'borrador'
                ? 'bg-naranja-100 text-marino-800'
                : v.estado === 'publicada'
                  ? 'bg-marino-700 text-white'
                  : 'bg-marino-100 text-marino-500'
            }`}
          >
            {v.estado === 'borrador'
              ? 'Borrador — todavía no lo ve nadie'
              : v.estado === 'publicada'
                ? 'Publicado — es lo que se responde'
                : 'Versión retirada — solo consulta'}
          </span>
        </div>
        {c.descripcion && <p className="mt-1 text-sm text-marino-400">{c.descripcion}</p>}

        {/*
          El historial de versiones, en palabras. Solo aparece cuando hay más de
          una: con una sola versión no hay nada entre lo que saltar, y un control
          que no ofrece elección es ruido.
        */}
        {lista.length > 1 && (
          <label className="mt-3 inline-flex items-center gap-2 text-sm text-marino-500">
            Viendo
            <select
              value={versionId ?? undefined}
              onChange={(e) => setElegida(Number(e.target.value))}
              className="rounded-xl border border-marino-200 bg-white px-3 py-1.5 text-sm font-medium
                text-marino-800 focus:outline-none focus:ring-4 focus:ring-marino-700/10"
            >
              {lista.map((ver) => (
                <option key={ver.id} value={ver.id}>
                  {ver.estado === 'borrador'
                    ? `Versión ${ver.version} — el borrador en trabajo`
                    : ver.estado === 'publicada'
                      ? `Versión ${ver.version} — la publicada`
                      : `Versión ${ver.version} — retirada`}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      {error && (
        <div
          role="alert"
          className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {!esBorrador && (
        <p className="mb-5 rounded-xl border border-marino-100 bg-marino-50 px-4 py-3 text-sm text-marino-600">
          {v.estado === 'publicada'
            ? 'Esta versión no se edita: puede tener respuestas, y cambiarla haría que el «antes» y el «después» dejaran de medir lo mismo. Para cambiar algo, «Editar» crea una versión nueva.'
            : 'Esta versión se retiró: ya no se ofrece para aplicaciones nuevas, pero se conserva porque las respuestas que se dieron apuntan a ella.'}
        </p>
      )}

      {/*
        La herramienta toma el espacio flexible; el teléfono, su ancho natural.
        En pantallas angostas se apilan: primero la herramienta —lo primero que
        se hace es escribir— y el teléfono debajo.
      */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
        <div>
          {esBorrador && puedeArmar ? (
            edicion ? (
              <EditorPregunta
                cuestionarioId={id}
                preguntaId={edicion.preguntaId}
                f={edicion.f}
                escalas={escalas.data ?? []}
                alCambiar={(f) => setEdicion({ ...edicion, f })}
                alCerrar={() => setEdicion(null)}
              />
            ) : (
              <PanelPreguntas
                version={v}
                alAgregar={(tipo) => setEdicion({ preguntaId: null, f: { ...VACIA, tipo } })}
                alEditar={(p) => setEdicion({ preguntaId: p.id, f: aBorrador(p) })}
                alQuitar={(preguntaId) =>
                  confirm('¿Quitar esto del borrador?') &&
                  void ejecutar(
                    () => acciones.quitar.mutateAsync(preguntaId),
                    'No pudimos quitarla.',
                  )
                }
                alMover={(desde, hasta) => {
                  const ids = v.preguntas.map((p) => p.id);
                  const [movida] = ids.splice(desde, 1);
                  if (movida === undefined) return;
                  ids.splice(hasta, 0, movida);
                  void ejecutar(
                    () => acciones.reordenar.mutateAsync(ids),
                    'No pudimos cambiar el orden.',
                  );
                }}
              />
            )
          ) : (
            <div className="rounded-2xl border border-marino-100 bg-white p-5">
              <h2 className="font-semibold text-marino-800">
                {v.preguntas.filter((p) => !esSeccion(p.tipo)).length} preguntas
              </h2>
              <p className="mt-1 text-sm text-marino-400">
                Esta versión no se edita. El teléfono la muestra tal como la ve quien responde —
                puedes recorrerla con «Comenzar» y «Siguiente».
              </p>
            </div>
          )}
        </div>

        <section aria-label="Vista previa" className="lg:sticky lg:top-4">
          <h2 className="mb-2 text-center text-xs font-semibold uppercase tracking-widest text-marino-400">
            Así se responde en el teléfono
          </h2>
          <VistaFormulario
            nombre={c.nombre}
            descripcion={c.descripcion}
            preguntas={preguntasEnPantalla}
            escalas={escalas.data ?? []}
            idResaltado={idResaltado}
          />
        </section>
      </div>
    </Contenedor>
  );
}

// ---------------------------------------------------------------------------
// Panel izquierdo — la lista
// ---------------------------------------------------------------------------

function PanelPreguntas({
  version,
  alAgregar,
  alEditar,
  alQuitar,
  alMover,
}: {
  version: Version;
  alAgregar: (tipo: TipoPregunta) => void;
  alEditar: (p: Pregunta) => void;
  alQuitar: (id: number) => void;
  alMover: (desde: number, hasta: number) => void;
}) {
  const total = version.preguntas.filter((p) => !esSeccion(p.tipo)).length;
  let numero = 0;

  return (
    <div className="rounded-2xl border border-marino-100 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold text-marino-800">
          {total} {total === 1 ? 'pregunta' : 'preguntas'}
        </h2>
        <div className="flex gap-2">
          {/* Dos altas distintas porque son dos gestos distintos: una pregunta
              se agrega a cada rato; una sección, unas pocas veces. */}
          <Boton
            variante="secundario"
            className="px-3 py-1.5 text-sm"
            onClick={() => alAgregar('seccion')}
          >
            Nueva sección
          </Boton>
          <Boton className="px-3 py-1.5 text-sm" onClick={() => alAgregar('likert')}>
            Agregar pregunta
          </Boton>
        </div>
      </div>

      {version.preguntas.length === 0 ? (
        <p className="rounded-xl border border-dashed border-marino-200 px-3 py-6 text-center text-sm text-marino-400">
          Agrega la primera pregunta. Las secciones parten el cuestionario en páginas, cada una con
          su título y su color.
        </p>
      ) : (
        <ol className="space-y-1">
          {version.preguntas.map((p, i) => {
            const seccion = esSeccion(p.tipo);
            if (!seccion) numero += 1;
            const acento = seccion ? ACENTOS[p.color ?? 'marino'] : undefined;
            return (
              <li
                key={p.id}
                className={`flex items-center gap-1.5 rounded-xl ${
                  seccion ? 'mt-3 first:mt-0' : ''
                } hover:bg-marino-50/70`}
              >
                <button
                  type="button"
                  onClick={() => alEditar(p)}
                  className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2 py-2 text-left
                    focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                >
                  {seccion ? (
                    <span
                      className="h-6 w-1.5 shrink-0 rounded-full"
                      style={{ background: acento?.fuerte }}
                    />
                  ) : (
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-marino-50 text-xs font-semibold text-marino-500">
                      {numero}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-sm ${
                        seccion ? 'font-bold uppercase tracking-wide text-marino-700' : 'text-marino-800'
                      }`}
                    >
                      {p.texto}
                    </span>
                    <span className="block truncate text-xs text-marino-400">
                      {seccion ? 'Sección — abre una página' : TIPO_PREGUNTA[p.tipo].titulo}
                      {!seccion && p.competencia && ` · ${p.competencia}`}
                    </span>
                  </span>
                </button>

                {/* Flechas y no arrastre: funciona con teclado y con lector de
                    pantalla, y no cuesta una librería. */}
                <div className="flex shrink-0 items-center">
                  <BotonIcono rotulo="Subir" desactivado={i === 0} onClick={() => alMover(i, i - 1)}>
                    ↑
                  </BotonIcono>
                  <BotonIcono
                    rotulo="Bajar"
                    desactivado={i === version.preguntas.length - 1}
                    onClick={() => alMover(i, i + 1)}
                  >
                    ↓
                  </BotonIcono>
                  <BotonIcono rotulo="Quitar" onClick={() => alQuitar(p.id)}>
                    <Equis size={14} />
                  </BotonIcono>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function BotonIcono({
  rotulo,
  desactivado,
  onClick,
  children,
}: {
  rotulo: string;
  desactivado?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={rotulo}
      aria-label={rotulo}
      disabled={desactivado}
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded-lg text-marino-400 transition
        hover:bg-white hover:text-marino-800 disabled:cursor-not-allowed disabled:opacity-25
        focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Panel izquierdo — el editor
// ---------------------------------------------------------------------------

type Borrador = {
  tipo: TipoPregunta;
  texto: string;
  ayuda: string;
  obligatoria: boolean;
  competenciaId: number | null;
  escalaId: number | null;
  minimo: string;
  maximo: string;
  color: ColorSeccion | null;
  emoji: string;
  items: string[];
};

const VACIA: Borrador = {
  tipo: 'likert',
  texto: '',
  ayuda: '',
  obligatoria: true,
  competenciaId: null,
  escalaId: null,
  minimo: '',
  maximo: '',
  color: null,
  emoji: '',
  items: [],
};

const aBorrador = (p: Pregunta): Borrador => ({
  tipo: p.tipo,
  texto: p.texto,
  ayuda: p.ayuda ?? '',
  obligatoria: p.obligatoria,
  competenciaId: p.competenciaId,
  escalaId: p.escalaId,
  minimo: p.minimo === null ? '' : String(p.minimo),
  maximo: p.maximo === null ? '' : String(p.maximo),
  color: (p.color as ColorSeccion | null) ?? null,
  emoji: p.emoji ?? '',
  items: p.items.map((i) => i.texto),
});

/**
 * El borrador del formulario con forma de `Pregunta`, para que el teléfono lo
 * pinte mientras se teclea.
 *
 * ⚠️ Los ítems reciben ids **negativos**: React necesita una clave estable por
 * fila y los ítems en curso no tienen id de la base todavía. Positivos podrían
 * chocar con ids reales del mismo listado.
 */
function aPreguntaPrevia(f: Borrador, id: number, orden: number): Pregunta {
  const num = (s: string) => (s.trim() === '' ? null : Number(s));
  return {
    id,
    orden,
    tipo: f.tipo,
    texto: f.texto,
    ayuda: f.ayuda.trim() || null,
    obligatoria: f.obligatoria,
    competenciaId: f.competenciaId,
    competencia: null,
    escalaId: usaEscala(f.tipo) ? f.escalaId : null,
    minimo: usaRango(f.tipo) ? num(f.minimo) : null,
    maximo: usaRango(f.tipo) ? num(f.maximo) : null,
    color: esSeccion(f.tipo) ? (f.color ?? 'marino') : null,
    emoji: f.emoji.trim() || null,
    items: usaItems(f.tipo)
      ? f.items.map((texto, i) => ({ id: -(i + 1), orden: i + 1, texto }))
      : [],
  };
}

function EditorPregunta({
  cuestionarioId,
  preguntaId,
  f,
  escalas,
  alCambiar,
  alCerrar,
}: {
  cuestionarioId: number;
  preguntaId: number | null;
  f: Borrador;
  escalas: Escala[];
  alCambiar: (f: Borrador) => void;
  alCerrar: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [campos, setCampos] = useState<Record<string, string>>({});

  const competencias = useCatalogo('competencia');
  const { crear: crearOpcion } = useGuardarOpcion();
  const acciones = useBorrador(cuestionarioId);

  const cambiar = <K extends keyof Borrador>(k: K, v: Borrador[K]) => alCambiar({ ...f, [k]: v });
  const seccion = esSeccion(f.tipo);

  // Escala por omisión cuando solo hay una activa: elegir de una lista de un
  // elemento es un paso que nunca aporta información.
  useEffect(() => {
    if (!usaEscala(f.tipo) || f.escalaId !== null) return;
    const activas = escalas.filter((e) => e.activo);
    const unica = activas.length === 1 ? activas[0] : undefined;
    if (unica) alCambiar({ ...f, escalaId: unica.id });
  }, [f.tipo, f.escalaId, escalas]);

  async function enviar() {
    setError(null);
    setCampos({});

    const num = (s: string) => (s.trim() === '' ? null : Number(s));
    const datos: EntradaGuardarPregunta = {
      tipo: f.tipo,
      texto: f.texto.trim(),
      ayuda: f.ayuda.trim() || null,
      obligatoria: seccion ? false : f.obligatoria,
      competenciaId: seccion ? null : f.competenciaId,
      escalaId: usaEscala(f.tipo) ? f.escalaId : null,
      minimo: usaRango(f.tipo) ? num(f.minimo) : null,
      maximo: usaRango(f.tipo) ? num(f.maximo) : null,
      color: seccion ? (f.color ?? 'marino') : null,
      emoji: f.emoji.trim() || null,
      items: usaItems(f.tipo) ? f.items.map((i) => i.trim()).filter(Boolean) : [],
    };

    try {
      if (preguntaId === null) await acciones.agregar.mutateAsync(datos);
      else await acciones.guardar.mutateAsync({ preguntaId, datos });
      alCerrar();
    } catch (e) {
      if (e instanceof ErrorApi) {
        setError(e.message);
        setCampos(e.campos ?? {});
        return;
      }
      setError('No pudimos guardar. Intenta de nuevo.');
    }
  }

  const rotulo =
    f.tipo === 'matriz_likert'
      ? { plural: 'Afirmaciones', singular: 'afirmación' }
      : { plural: 'Opciones', singular: 'opción' };

  return (
    <div className="rounded-2xl border border-marino-100 bg-white p-4">
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={alCerrar}
          className="flex h-8 w-8 items-center justify-center rounded-xl border border-marino-100
            text-marino-500 transition hover:border-marino-300 hover:text-marino-800
            focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
          aria-label="Volver a la lista"
          title="Volver a la lista"
        >
          <Flecha size={15} />
        </button>
        <h2 className="font-semibold text-marino-800">
          {preguntaId === null
            ? seccion
              ? 'Nueva sección'
              : 'Nueva pregunta'
            : seccion
              ? 'Editar sección'
              : 'Editar pregunta'}
        </h2>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <div className="space-y-4">
        <SelectorTipo tipo={f.tipo} alElegir={(t) => cambiar('tipo', t)} />

        <Campo
          etiqueta={
            seccion ? 'Título de la sección' : f.tipo === 'matriz_likert' ? 'Enunciado de la matriz' : 'Pregunta'
          }
          value={f.texto}
          onChange={(e) => cambiar('texto', e.target.value)}
          error={campos.texto}
          autoFocus
        />

        <Campo
          etiqueta={seccion ? 'Subtítulo (opcional)' : 'Ayuda (opcional)'}
          value={f.ayuda}
          onChange={(e) => cambiar('ayuda', e.target.value)}
          error={campos.ayuda}
        />

        <SelectorEmoji valor={f.emoji} alElegir={(e) => cambiar('emoji', e)} />

        {seccion && (
          <SelectorColor
            valor={f.color ?? 'marino'}
            alElegir={(color) => cambiar('color', color)}
          />
        )}

        {!seccion && (
          <Combo
            etiqueta="Competencia que mide"
            opciones={(competencias.data ?? []).map((c) => ({ clave: c.id, texto: c.nombre }))}
            valor={f.competenciaId}
            alCambiar={(v) => cambiar('competenciaId', v === null ? null : Number(v))}
            alCrear={async (texto) => {
              const creada = await crearOpcion.mutateAsync({ tipo: 'competencia', nombre: texto });
              cambiar('competenciaId', creada.id);
              return { clave: creada.id, texto: creada.nombre };
            }}
            crearRotulo="Agregar competencia"
            vacio="Sin competencia"
            error={campos.competenciaId}
          />
        )}

        {usaEscala(f.tipo) && (
          <Combo
            etiqueta="Escala"
            opciones={escalas
              .filter((e) => e.activo)
              .map((e) => ({ clave: e.id, texto: e.nombre, detalle: `${e.puntos.length} puntos` }))}
            valor={f.escalaId}
            alCambiar={(v) => cambiar('escalaId', v === null ? null : Number(v))}
            vacio="Elige una escala"
            error={campos.escalaId}
          />
        )}

        {usaItems(f.tipo) && (
          <EditorItems
            plural={rotulo.plural}
            singular={rotulo.singular}
            items={f.items}
            error={campos.items}
            alCambiar={(items) => cambiar('items', items)}
          />
        )}

        {usaRango(f.tipo) && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etiqueta={f.tipo === 'numero' ? 'Valor mínimo' : 'Mínimo a elegir'}
              type="number"
              value={f.minimo}
              onChange={(e) => cambiar('minimo', e.target.value)}
              error={campos.minimo}
            />
            <Campo
              etiqueta={f.tipo === 'numero' ? 'Valor máximo' : 'Máximo a elegir'}
              type="number"
              value={f.maximo}
              onChange={(e) => cambiar('maximo', e.target.value)}
              error={campos.maximo}
            />
          </div>
        )}

        {!seccion && (
          <label className="flex items-center gap-3 text-sm text-marino-700">
            <input
              type="checkbox"
              checked={f.obligatoria}
              onChange={(e) => cambiar('obligatoria', e.target.checked)}
              className="h-4 w-4 rounded border-marino-300 text-naranja-500 focus:ring-marino-700/15"
            />
            Obligatoria
          </label>
        )}
      </div>

      <div className="mt-5 flex justify-end gap-2 border-t border-marino-100 pt-4">
        <Boton variante="secundario" className="px-3.5 py-2 text-sm" onClick={alCerrar}>
          Cancelar
        </Boton>
        <Boton
          className="px-4 py-2 text-sm"
          cargando={acciones.agregar.isPending || acciones.guardar.isPending}
          onClick={() => void enviar()}
        >
          Guardar
        </Boton>
      </div>
    </div>
  );
}

/** Los diez tipos, agrupados en cuatro familias. */
function SelectorTipo({
  tipo,
  alElegir,
}: {
  tipo: TipoPregunta;
  alElegir: (t: TipoPregunta) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-semibold text-marino-700">Tipo</legend>
      <div className="space-y-2.5">
        {FAMILIAS_PREGUNTA.map((familia) => (
          <div key={familia.clave}>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-marino-400">
              {familia.titulo}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {TIPOS_PREGUNTA.filter((t) => TIPO_PREGUNTA[t].familia === familia.clave).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => alElegir(t)}
                  aria-pressed={tipo === t}
                  title={TIPO_PREGUNTA[t].ayuda}
                  className={`rounded-xl border px-2.5 py-1.5 text-sm font-medium transition
                    focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                    ${
                      tipo === t
                        ? 'border-naranja-500 bg-naranja-50 text-marino-800'
                        : 'border-marino-100 text-marino-500 hover:border-marino-300 hover:text-marino-800'
                    }`}
                >
                  {TIPO_PREGUNTA[t].titulo}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-marino-400">{TIPO_PREGUNTA[tipo].ayuda}</p>
    </fieldset>
  );
}

/**
 * Sugerencias de emoji, no un selector completo.
 *
 * Un picker de mil emojis es una dependencia y una decisión de más por pregunta.
 * Estas veinte cubren lo que un cuestionario de habilidades blandas de verdad
 * usa, y el campo acepta CUALQUIER emoji pegado del teclado del sistema
 * (Win + . en Windows) — que ya es el mejor picker que existe.
 */
const EMOJIS_SUGERIDOS = [
  '💬', '👂', '🤝', '💡', '🎯', '🌱', '🧭', '❤️', '⭐', '🏆',
  '👥', '🗣️', '🧠', '⚖️', '🔥', '😄', '🙌', '📣', '🤔', '✨',
] as const;

function SelectorEmoji({ valor, alElegir }: { valor: string; alElegir: (e: string) => void }) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-sm font-semibold text-marino-700">
        Emoji (opcional)
      </legend>
      <div className="flex flex-wrap items-center gap-1">
        {EMOJIS_SUGERIDOS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => alElegir(valor === e ? '' : e)}
            aria-pressed={valor === e}
            className={`grid h-8 w-8 place-items-center rounded-lg text-lg transition
              focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
              ${valor === e ? 'bg-naranja-100 ring-2 ring-naranja-500' : 'hover:bg-marino-50'}`}
          >
            {e}
          </button>
        ))}
        <input
          value={valor}
          onChange={(e) => alElegir(e.target.value)}
          maxLength={16}
          placeholder="otro…"
          aria-label="Otro emoji"
          className="h-8 w-16 rounded-lg border border-marino-200 px-2 text-center text-sm
            focus:outline-none focus:ring-4 focus:ring-marino-700/10"
        />
      </div>
      <p className="mt-1 text-xs text-marino-400">
        Acompaña al enunciado. En Windows, Win + . abre el teclado de emojis.
      </p>
    </fieldset>
  );
}

/**
 * La paleta de acentos, como muestras que se ven — un `<select>` con nombres de
 * colores obliga a imaginárselos.
 */
function SelectorColor({
  valor,
  alElegir,
}: {
  valor: ColorSeccion;
  alElegir: (c: ColorSeccion) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-semibold text-marino-700">Color de la sección</legend>
      <div className="flex flex-wrap gap-2">
        {COLORES_SECCION.map((nombre) => {
          const acento = ACENTOS[nombre]!;
          const activo = valor === nombre;
          return (
            <button
              key={nombre}
              type="button"
              onClick={() => alElegir(nombre)}
              aria-pressed={activo}
              aria-label={`Color ${nombre}`}
              title={nombre}
              className="grid h-9 w-9 place-items-center rounded-full transition
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
              style={{
                background: acento.suave,
                boxShadow: activo ? `0 0 0 3px white, 0 0 0 5px ${acento.fuerte}` : undefined,
              }}
            >
              <span className="h-5 w-5 rounded-full" style={{ background: acento.fuerte }} />
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-xs text-marino-400">
        Tiñe la página que abre esta sección: la cabecera, los números y los botones.
      </p>
    </fieldset>
  );
}

function EditorItems({
  plural,
  singular,
  items,
  error,
  alCambiar,
}: {
  plural: string;
  singular: string;
  items: string[];
  error?: string | undefined;
  alCambiar: (items: string[]) => void;
}) {
  const cambiarUno = (i: number, texto: string) =>
    alCambiar(items.map((v, j) => (i === j ? texto : v)));
  const Singular = singular.charAt(0).toUpperCase() + singular.slice(1);

  return (
    <fieldset>
      <legend className="mb-2 text-sm font-semibold text-marino-700">{plural}</legend>

      <div className="space-y-2">
        {items.map((texto, i) => (
          // El índice como clave es correcto acá y solo acá: la lista se reordena
          // por posición y el valor vive en este mismo arreglo.
          <div key={i} className="flex items-center gap-1.5">
            <Campo
              etiqueta={`${Singular} ${i + 1}`}
              value={texto}
              onChange={(e) => cambiarUno(i, e.target.value)}
              className="flex-1"
            />
            <BotonIcono
              rotulo={`Quitar ${singular} ${i + 1}`}
              onClick={() => alCambiar(items.filter((_, j) => j !== i))}
            >
              <Equis size={14} />
            </BotonIcono>
          </div>
        ))}
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <button
        type="button"
        onClick={() => alCambiar([...items, ''])}
        className="mt-2.5 text-sm font-semibold text-marino-600 transition hover:text-naranja-600 hover:underline"
      >
        Agregar {singular}
      </button>
    </fieldset>
  );
}
